import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';

import { FEATURE } from '~/common/enums/feature.enum';
import { TransactionType } from '~/common/enums/payments.enum';
import { UserContext } from '~/components/providers/UserProvider';
import { useTreatments } from '~/domains/common/hooks/useTreatments';
import type { PaymentType } from '~/services/wallet/interfaces/wallets.interface';
import { CardCategory } from '~/services/wallet/interfaces/wallets.interface';
import { getPaymentsMethods } from '~/services/wallet/wallet.service.api';

import { PAYMENTS_QUERY_STALE_TIME } from './consts';

type UsePaymentMethodsArgs = {
  transactionType: TransactionType;
  paymentType: PaymentType;
};

export function usePaymentMethods({ transactionType, paymentType }: UsePaymentMethodsArgs) {
  const { user } = useContext(UserContext);
  const { isSettled, treatments } = useTreatments([FEATURE.SHOW_SAVED_CREDIT_CARDS_ON_DEPOSIT]);
  const showSavedCreditCardsOnDeposit =
    treatments[FEATURE.SHOW_SAVED_CREDIT_CARDS_ON_DEPOSIT]?.treatment === 'on';
  // Until the flag settles it reads as off, so a deposit would pick its default card from a
  // list missing the saved credit cards. A timed-out SDK settles as off.
  const isDepositFlagPending = transactionType === TransactionType.DEPOSIT && !isSettled;

  const { data, ...query } = useQuery({
    queryKey: ['payments/paymentMethods', user?.id],
    queryFn: getPaymentsMethods,
    staleTime: PAYMENTS_QUERY_STALE_TIME,
  });

  const paymentMethods = useMemo(() => {
    if (!data) return null;

    const { paymentHandles } = data.data;

    // Withdrawals to credit cards are not enabled. Deposits with credit cards are declined by
    // the payment provider, so saved credit cards are hidden unless the flag turns them back on.
    const hideCreditCards =
      transactionType === TransactionType.WITHDRAWAL || !showSavedCreditCardsOnDeposit;

    const filtered = paymentHandles.filter((paymentHandle) => {
      if (paymentHandle.paymentType !== paymentType) {
        return false;
      }

      if (hideCreditCards && paymentHandle.card.cardCategory === CardCategory.CREDIT) {
        return false;
      }

      return true;
    });

    return filtered;
  }, [data, paymentType, transactionType, showSavedCreditCardsOnDeposit]);

  return {
    ...query,
    isLoading: query.isLoading || isDepositFlagPending,
    data: paymentMethods,
  };
}
